import { initializeApp } from "firebase/app";
import {
  ConfirmationResult,
  EmailAuthProvider,
  RecaptchaVerifier,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  linkWithCredential,
  linkWithPhoneNumber,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signOut
} from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyD4E3nh2WcpX-w-9NkxnkJ7SmdahoC6iRQ",
  authDomain: "mya1-b8767.firebaseapp.com",
  projectId: "mya1-b8767",
  storageBucket: "mya1-b8767.firebasestorage.app",
  messagingSenderId: "948466355147",
  appId: "1:948466355147:web:732a7386e86f66514a044e",
  measurementId: "G-S8T1W2KDNC"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

const identityText = document.querySelector<HTMLParagraphElement>("#identityText");
const providerList = document.querySelector<HTMLSpanElement>("#providerList");
const emailTabBtn = document.querySelector<HTMLButtonElement>("#emailTabBtn");
const phoneTabBtn = document.querySelector<HTMLButtonElement>("#phoneTabBtn");
const emailPanel = document.querySelector<HTMLElement>("#emailPanel");
const phonePanel = document.querySelector<HTMLElement>("#phonePanel");
const emailInput = document.querySelector<HTMLInputElement>("#emailInput");
const passwordInput = document.querySelector<HTMLInputElement>("#passwordInput");
const emailAuthBtn = document.querySelector<HTMLButtonElement>("#emailAuthBtn");
const phoneInput = document.querySelector<HTMLInputElement>("#phoneInput");
const codeInput = document.querySelector<HTMLInputElement>("#codeInput");
const sendCodeBtn = document.querySelector<HTMLButtonElement>("#sendCodeBtn");
const verifyCodeBtn = document.querySelector<HTMLButtonElement>("#verifyCodeBtn");
const logoutBtn = document.querySelector<HTMLButtonElement>("#logoutBtn");
const resultBox = document.querySelector<HTMLPreElement>("#resultBox");

let recaptchaVerifier: RecaptchaVerifier;
let confirmationResult: ConfirmationResult | null = null;
let pendingPhoneAction: "sign-in" | "link-phone" = "sign-in";

function assertDom() {
  if (
    !emailInput ||
    !passwordInput ||
    !emailAuthBtn ||
    !identityText ||
    !providerList ||
    !emailTabBtn ||
    !phoneTabBtn ||
    !emailPanel ||
    !phonePanel ||
    !phoneInput ||
    !codeInput ||
    !sendCodeBtn ||
    !verifyCodeBtn ||
    !logoutBtn ||
    !resultBox
  ) {
    throw new Error("页面缺少必要 DOM 元素，请检查 index.html");
  }
}

function showResult(value: unknown) {
  if (!resultBox) {
    return;
  }
  if (typeof value === "string") {
    resultBox.textContent = value;
    return;
  }
  resultBox.textContent = JSON.stringify(value, null, 2);
}

function normalizePhone(rawValue: string): string {
  const value = rawValue.trim();
  if (!value.startsWith("+")) {
    throw new Error("手机号必须是 E.164 格式，以 + 开头");
  }
  return value;
}

function normalizeEmail(rawValue: string): string {
  const value = rawValue.trim();
  if (!value.includes("@")) {
    throw new Error("请输入有效邮箱地址");
  }
  return value;
}

function normalizePassword(rawValue: string): string {
  const value = rawValue.trim();
  if (value.length < 6) {
    throw new Error("密码长度至少 6 位");
  }
  return value;
}

function providerIdToName(providerId: string): string {
  switch (providerId) {
    case "phone":
      return "手机号";
    case "password":
      return "邮箱密码";
    case "emailLink":
      return "邮箱免密链接";
    default:
      return providerId;
  }
}

function getProviderDisplayText() {
  const user = auth.currentUser;
  if (!user) {
    return "(none)";
  }

  const ids = user.providerData.map((item) => item.providerId);
  const uniqueIds = Array.from(new Set(ids));
  if (uniqueIds.length === 0) {
    return "(none)";
  }

  return uniqueIds.map(providerIdToName).join(", ");
}

function updateIdentitySummary() {
  if (!identityText || !providerList) {
    return;
  }

  const user = auth.currentUser;
  if (!user) {
    identityText.textContent = "未登录";
    providerList.textContent = "(none)";
    return;
  }

  identityText.textContent = `uid: ${user.uid}`;
  providerList.textContent = getProviderDisplayText();
}

function setActiveTab(tab: "email" | "phone") {
  if (!emailTabBtn || !phoneTabBtn || !emailPanel || !phonePanel) {
    return;
  }

  const isEmail = tab === "email";
  emailTabBtn.classList.toggle("active", isEmail);
  phoneTabBtn.classList.toggle("active", !isEmail);
  emailTabBtn.setAttribute("aria-selected", isEmail ? "true" : "false");
  phoneTabBtn.setAttribute("aria-selected", isEmail ? "false" : "true");

  emailPanel.classList.toggle("panel-hidden", !isEmail);
  phonePanel.classList.toggle("panel-hidden", isEmail);
}

function showLinkingContext(target: "邮箱" | "手机号") {
  const user = auth.currentUser;
  showResult({
    status: `准备关联${target}`,
    uid: user?.uid ?? null,
    currentProviders: getProviderDisplayText()
  });
}

function getAuthErrorCode(error: unknown): string | null {
  if (!error || typeof error !== "object") {
    return null;
  }

  const code = (error as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}

function getFriendlyAuthError(error: unknown): string {
  const code = getAuthErrorCode(error);
  switch (code) {
    case "auth/operation-not-allowed":
      return "当前项目未启用该登录方式。请到 Firebase Console -> Authentication -> Sign-in method 启用 Email/Password 后重试。";
    case "auth/email-already-in-use":
      return "该邮箱已被其他账号使用。请先登出并使用该邮箱登录，再执行关联。";
    case "auth/credential-already-in-use":
      return "该凭证已绑定到其他账号。请先登录原账号，再进行账户合并/关联。";
    case "auth/provider-already-linked":
      return "当前账号已绑定该登录方式，无需重复关联。";
    case "auth/wrong-password":
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
      return "邮箱或密码不正确，请检查后重试。";
    default:
      if (error instanceof Error && error.message) {
        return error.message;
      }
      return String(error);
  }
}

async function initAuth() {
  await setPersistence(auth, browserSessionPersistence);

  // 使用 "invisible" 模式，验证码容器可以隐藏或由 SDK 自动触发
  recaptchaVerifier = new RecaptchaVerifier(auth, "sendCodeBtn", {
    size: "invisible",
    callback: () => {
      // reCAPTCHA 验证通过
    }
  });

  onAuthStateChanged(auth, (user) => {
    updateIdentitySummary();

    if (!user) {
      showResult("当前未登录。请先发送验证码并确认登录。");
      return;
    }

    showResult({
      status: "登录成功",
      uid: user.uid,
      phoneNumber: user.phoneNumber,
      email: user.email,
      providers: getProviderDisplayText(),
      isAnonymous: user.isAnonymous
    });
  });
}

async function handleSendCode() {
  try {
    if (!phoneInput) {
      return;
    }

    const currentUser = auth.currentUser;
    const phoneNumber = normalizePhone(phoneInput.value);
    sendCodeBtn?.setAttribute("disabled", "true");
    showResult("正在发送验证码...");

    if (currentUser && !currentUser.phoneNumber) {
      showLinkingContext("手机号");
      pendingPhoneAction = "link-phone";
      confirmationResult = await linkWithPhoneNumber(currentUser, phoneNumber, recaptchaVerifier);
      showResult("验证码已发送，请输入短信验证码完成手机号关联。");
      return;
    }

    pendingPhoneAction = "sign-in";
    confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    showResult("验证码已发送，请输入短信中的验证码后点击确认登录。");
  } catch (error) {
    const message = getFriendlyAuthError(error);
    showResult(`发送验证码失败: ${message}`);
  } finally {
    sendCodeBtn?.removeAttribute("disabled");
  }
}

async function handleVerifyCode() {
  try {
    if (!confirmationResult || !codeInput) {
      throw new Error("请先发送验证码");
    }

    const code = codeInput.value.trim();
    if (!code) {
      throw new Error("请输入验证码");
    }

    showResult("正在验证验证码...");
    const userCredential = await confirmationResult.confirm(code);

    if (pendingPhoneAction === "link-phone") {
      showResult({
        status: "手机号关联成功",
        uid: userCredential.user.uid,
        phoneNumber: userCredential.user.phoneNumber,
        email: userCredential.user.email,
        providers: getProviderDisplayText()
      });
      return;
    }

    showResult({
      status: "手机号登录成功",
      uid: userCredential.user.uid,
      phoneNumber: userCredential.user.phoneNumber,
      email: userCredential.user.email,
      providers: getProviderDisplayText()
    });
  } catch (error) {
    const message = getFriendlyAuthError(error);
    showResult(`验证失败: ${message}`);
  }
}

async function handleEmailAuth() {
  try {
    if (!emailInput || !passwordInput) {
      return;
    }

    const email = normalizeEmail(emailInput.value);
    const password = normalizePassword(passwordInput.value);
    emailAuthBtn?.setAttribute("disabled", "true");

    const currentUser = auth.currentUser;
    if (currentUser) {
      if (currentUser.email) {
        showResult("当前账号已绑定邮箱，无需重复关联。可直接登出后切换账号。");
        return;
      }

      showLinkingContext("邮箱");
      const credential = EmailAuthProvider.credential(email, password);
      const linked = await linkWithCredential(currentUser, credential);
      showResult({
        status: "邮箱关联成功",
        uid: linked.user.uid,
        email: linked.user.email,
        phoneNumber: linked.user.phoneNumber,
        providers: getProviderDisplayText()
      });
      return;
    }

    try {
      const signedIn = await signInWithEmailAndPassword(auth, email, password);
      showResult({
        status: "邮箱登录成功",
        uid: signedIn.user.uid,
        email: signedIn.user.email,
        phoneNumber: signedIn.user.phoneNumber,
        providers: getProviderDisplayText()
      });
      return;
    } catch (signInError) {
      const signInCode = getAuthErrorCode(signInError);
      const canAutoCreate = signInCode === "auth/user-not-found";
      if (!canAutoCreate) {
        throw signInError;
      }

      const created = await createUserWithEmailAndPassword(auth, email, password);
      showResult({
        status: "邮箱账号已创建并登录",
        uid: created.user.uid,
        email: created.user.email,
        phoneNumber: created.user.phoneNumber,
        providers: getProviderDisplayText()
      });
    }
  } catch (error) {
    const message = getFriendlyAuthError(error);
    showResult(`邮箱登录/关联失败: ${message}`);
  } finally {
    emailAuthBtn?.removeAttribute("disabled");
  }
}

async function handleLogout() {
  try {
    await signOut(auth);
    confirmationResult = null;
    showResult("已登出。");
  } catch (error) {
    const message = getFriendlyAuthError(error);
    showResult(`登出失败: ${message}`);
  }
}

async function bootstrap() {
  assertDom();
  setActiveTab("email");
  await initAuth();

  emailTabBtn?.addEventListener("click", () => {
    setActiveTab("email");
  });

  phoneTabBtn?.addEventListener("click", () => {
    setActiveTab("phone");
  });

  sendCodeBtn?.addEventListener("click", () => {
    void handleSendCode();
  });

  emailAuthBtn?.addEventListener("click", () => {
    void handleEmailAuth();
  });

  verifyCodeBtn?.addEventListener("click", () => {
    void handleVerifyCode();
  });

  logoutBtn?.addEventListener("click", () => {
    void handleLogout();
  });
}

void bootstrap();
